/**
 * Hook for Socket.IO real-time election vote updates.
 *
 * Listens for "elections:vote_update" events and updates the
 * cityhall-state React Query cache in-place (no HTTP refetch).
 */
import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { getSocket, connectSocket } from "../socket";
import type { CityHallState } from "../models/apiTypes";

interface VoteUpdatePayload {
  candidate_id: number;
  votes_count: number;
}

export function useElectionSocket(): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    const socket = getSocket();

    const onVoteUpdate = (data: VoteUpdatePayload) => {
      queryClient.setQueryData<CityHallState>(
        ["cityhall-state"],
        (old) => {
          if (!old) return old;
          return {
            ...old,
            candidates: (old.candidates ?? []).map((c) =>
              c.id === data.candidate_id
                ? { ...c, votes_count: data.votes_count }
                : c
            ),
          };
        }
      );
    };

    socket.on("elections:vote_update", onVoteUpdate);
    connectSocket();

    return () => {
      socket.off("elections:vote_update", onVoteUpdate);
    };
  }, [queryClient]);
}
